<template>
  <a-dropdown
    v-model:open="isEditing"
    :trigger="['click']"
    placement="rightTop"
  >
    <div
      class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
      @click.stop="handleEdit"
    >
      <ThemeImage :icon="imageNames.EDIT" alt="edit_icon" class="size-5" />
      <span class="text-sm text_color">Edit property</span>
    </div>
    <template #overlay>
      <div class="action_edit_dropdown_content" @mousedown.stop>
        <div
          class="flex flex-col w-[280px] h-[300px]"
          @click.stop
          @mousedown.stop
        >
          <!-- Selected actions list -->
          <div
            class="flex flex-col px-3 pt-3 pb-2 w-full flex-1 min-h-0 border_bottom"
          >
            <span class="secondary_color text-[11px] mb-2 px-4 font-semibold"
              >EDIT PROPERTY</span
            >

            <div
              class="flex flex-col gap-1 max-h-[200px] overflow-y-auto custom_scrollbar pb-2"
              :class="selectedActions.length === 0 ? 'items-center justify-center flex-1' : ''"
            >
              <EmptyScreen
                v-if="selectedActions.length === 0"
                source="action-edit-empty"
                text="No actions added"
                :text-size="12"
                css="size-8"
                padding="my-4"
              />
              <VueDraggableNext
                v-else
                :list="selectedActions"
                handle=".drag-handle"
                ghost-class="ghost-card"
                class="flex flex-col gap-1"
                @end="markChanged"
              >
                <div
                  v-for="action in selectedActions"
                  :key="action.id"
                  class="flex items-center gap-2 px-2 py-2 rounded dropdown_item"
                >
                  <ThemeImage
                    :icon="imageNames.DRAGGABLE_DOTS"
                    alt="drag_dots"
                    class="drag-handle cursor-grab active:cursor-grabbing size-4 flex-shrink-0"
                  />
                  <ThemeImage
                    :icon="action.icon"
                    alt="action_icon"
                    class="size-4 flex-shrink-0"
                  />
                  <span class="text-sm text_color truncate">{{
                    action.label
                  }}</span>
                  <ThemeImage
                    :icon="imageNames.CROSS_GREY"
                    alt="remove"
                    class="size-4 ml-auto cursor-pointer flex-shrink-0"
                    @click.stop="removeAction(action.id)"
                  />
                </div>
              </VueDraggableNext>
            </div>
          </div>

          <!-- Add Action button pinned to bottom -->
          <div class="py-2 px-3 mt-auto">
            <a-dropdown
              v-model:open="showAddActions"
              :trigger="availableActions.length > 0 ? ['click'] : []"
              :disabled="availableActions.length === 0"
              placement="rightTop"
            >
              <div
                class="flex items-center gap-2 dropdown_item rounded"
                :class="
                  availableActions.length > 0
                    ? 'cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                "
                @click.stop
              >
                <ThemeImage
                  :icon="imageNames.PLUS"
                  alt="plus_icon"
                  class="size-4"
                />
                <span class="text-sm text_color">Add Action</span>
              </div>
              <template #overlay>
                <div
                  class="action_edit_dropdown_content"
                  @mousedown.stop
                  @click.stop
                >
                  <div class="flex flex-col w-[260px] px-3 py-3">
                    <span
                      class="secondary_color text-[11px] mb-2 px-2 font-semibold"
                      >ACTIONS</span
                    >
                    <div
                      class="flex flex-col gap-1 max-h-[150px] overflow-y-auto custom_scrollbar"
                    >
                      <div
                        v-for="action in availableActions"
                        :key="action.id"
                        class="flex items-center gap-2 px-2 py-2 cursor-pointer dropdown_item rounded flex-shrink-0"
                        @click.stop="addAction(action.id)"
                      >
                        <ThemeImage
                          :icon="action.icon"
                          alt="action_icon"
                          class="size-4 flex-shrink-0"
                        />
                        <span class="text-sm text_color">{{
                          action.label
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </a-dropdown>
          </div>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { VueDraggableNext } from "vue-draggable-next";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import {
  actionPropertyOptions,
  getActionOptionsForEntity,
} from "@/src/constants/componentConstants.js";
import CRMService from "@/src/api/CRMService.js";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps({
  propertyMeta: {
    type: Object,
    default: null,
  },
  entityType: {
    type: String,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  service: {
    type: String,
    default: null,
  },
  isDynamicObject: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["property-updated", "close"]);

const isEditing = ref(false);
const showAddActions = ref(false);
const selectedActions = ref([]);
const initialActions = ref([]);
const hasChangesFlag = ref(false);

const allActions = computed(() =>
  props.entityType
    ? getActionOptionsForEntity(props.entityType)
    : actionPropertyOptions,
);

const availableActions = computed(() =>
  allActions.value.filter(
    (a) => !selectedActions.value.some((s) => s.id === a.id),
  ),
);

const markChanged = () => {
  hasChangesFlag.value = true;
};

const handleEdit = () => {
  if (!props.isDynamicObject) {
    message.warning("Action editing is only available for custom objects");
    return;
  }

  // If already editing, close (save will trigger via watcher)
  if (isEditing.value) {
    isEditing.value = false;
    return;
  }

  // Initialize and open
  const actionIds = props.propertyMeta?.metadata?.actions || [];
  selectedActions.value = actionIds
    .map((id) => allActions.value.find((a) => a.id === id))
    .filter(Boolean);
  initialActions.value = [...actionIds];
  hasChangesFlag.value = false;
  showAddActions.value = false;
  isEditing.value = true;
};

const addAction = (actionId) => {
  const action = allActions.value.find((a) => a.id === actionId);
  if (action) {
    selectedActions.value.push(action);
    hasChangesFlag.value = true;
  }
  if (availableActions.value.length === 0) {
    showAddActions.value = false;
  }
};

const removeAction = (actionId) => {
  selectedActions.value = selectedActions.value.filter(
    (a) => a.id !== actionId,
  );
  hasChangesFlag.value = true;
};

const hasChanges = () => {
  if (!hasChangesFlag.value) return false;
  const currentIds = selectedActions.value.map((a) => a.id);
  return JSON.stringify(currentIds) !== JSON.stringify(initialActions.value);
};

const refreshSchemaStore = async () => {
  await store.dispatch("schema/refreshSchema", {
    slug: props.entityType,
    projectId: props.projectId || null,
    service: props.service || null,
  });
};

const saveActions = async () => {
  if (
    !hasChanges() ||
    !props.isDynamicObject ||
    !props.entityType ||
    !props.propertyMeta?.slug
  ) {
    return;
  }

  try {
    const payload = {
      ...props.propertyMeta,
      actions: selectedActions.value.map((a) => a.id),
      created_by_id: Number(localStorage.getItem("userId")) || 1,
    };

    const response = await CRMService.updateSchemaObjectAttribute(
      props.entityType,
      props.propertyMeta.slug,
      payload,
      props.service,
    );

    if (response.success) {
      await refreshSchemaStore();
      emit("property-updated", {
        propertyMeta: props.propertyMeta,
        newName: props.propertyMeta.name,
        response: response,
        isActionChange: true,
      });
    } else {
      message.error(response.error || "Failed to update actions");
    }
  } catch (error) {
    console.error("Error updating actions:", error);
    message.error("Failed to update actions");
  }
};

// Save when dropdown closes
watch(isEditing, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    saveActions();
  }
});
</script>

<style>
.action_edit_dropdown_content {
  background: var(--dropdown-bg);
  border-radius: 8px;
  box-shadow: var(--dropdown-boxshadow);
  width: fit-content;
  max-height: 500px;
  overflow-y: auto;
}
</style>
